import {useMemo} from 'react';
import type {BreadcrumbItem} from 'src/app/layout/BackBar';
import {PathUtility} from 'src/shared/constants/PathBuilder';

export interface UseMyActionsBreadcrumbsOptions {
  /** The action item (for view mode) */
  item?: {id?: number; subject?: string};
}

interface UseMyActionsBreadcrumbsResult {
  backBarProps: {fallbackTo: string; breadcrumbs: BreadcrumbItem[]};
}

/**
 * Hook to generate consistent BackBar props for the user-facing Action view page
 * under the My Actions tab.
 *
 * The user path is: /dashboard/actions/user/{id}/view/{view}
 * Navigation flows back to the My Actions list.
 */
export const useMyActionsBreadcrumbs = ({
  item,
}: UseMyActionsBreadcrumbsOptions): UseMyActionsBreadcrumbsResult => {
  const backBarProps = useMemo(() => {
    const myActionsPath = PathUtility.Actions('user');

    const breadcrumbs: BreadcrumbItem[] = [
      {label: 'My Actions', to: myActionsPath},
    ];

    // Add the current item as the last breadcrumb (no link since we're on this page)
    breadcrumbs.push({label: item?.subject || 'View Action'});

    return {
      fallbackTo: myActionsPath,
      breadcrumbs,
    };
  }, [item?.subject]);

  return {backBarProps};
};
